<template>
  <div class="video-tutor-container">
    <!-- 主页内容 -->
    <div v-if="!showVideoPlayer && !isGeneratingVideo">
      <div class="header-section">
        <h1 class="main-title">万物皆可问，知识一目了然</h1>

        <div class="search-box">
          <el-input
            placeholder="试试输入：什么是熵？ 什么是三角函数？"
            v-model="searchQuery"
            class="search-input"
            @keyup.enter.native="handleSearch"
            @focus="showSearchSuggestions"
          >
            <el-button slot="append" icon="el-icon-search" @click="handleSearch">立即开始提问</el-button>
          </el-input>
          <div v-if="showSuggestions" class="search-suggestions">
            <div class="suggestion-item" @click="selectSuggestion('什么是三角形的勾股定理')">什么是三角形的勾股定理</div>
            <div class="suggestion-item" @click="selectSuggestion('什么是泰勒公式')">什么是泰勒公式</div>
          </div>
        </div>
      </div>

      <div class="category-section">
        <h3 class="section-title">支持主题：</h3>

        <div class="category-cards">
          <el-card class="category-card" shadow="hover" v-for="(category, index) in categories" :key="index">
            <div class="card-content">
              <div class="card-icon" :class="category.iconClass">{{ category.icon }}</div>
              <div class="card-info">
                <h4 class="card-title">{{ category.title }}</h4>
                <p class="card-desc">{{ category.description }}</p>
              </div>
            </div>
          </el-card>
        </div>
      </div>

      <div class="footer-section">
        <p class="footer-text">全球首个教育助手</p>
        <p class="footer-desc">完美的AI 聊天体验，答案教学，科学与语言一一完美适用于 SAT、AP 及更多考试。</p>
      </div>
    </div>

    <!-- 视频生成中提示 -->
    <div v-else-if="isGeneratingVideo" class="generating-video-container">
      <div class="generating-content">
        <i class="el-icon-loading generating-icon"></i>
        <h2 class="generating-title">正在生成AI教学视频</h2>
        <p class="generating-desc">请稍候，我们正在为您精心制作个性化教学视频...</p>
        <el-progress :percentage="generatingProgress" :show-text="false"></el-progress>
        <p class="progress-text">{{ Math.floor(generatingProgress) }}%</p>
      </div>
    </div>

    <!-- 视频播放界面 -->
    <div v-else class="video-player-container">
      <div class="video-content">
        <!-- 标签切换 -->
        <div class="video-tabs">
          <el-button type="primary" icon="el-icon-video-play" class="tab-button active">视频</el-button>
          <el-button type="info" icon="el-icon-edit-outline" class="tab-button">测试</el-button>
        </div>

        <!-- 视频播放区域 -->
        <div class="video-player">
          <video ref="videoPlayer" controls class="player">
            <source :src="currentVideo.src" type="video/mp4">
            您的浏览器不支持视频播放
          </video>
        </div>

        <!-- 视频标题和信息 -->
        <div class="video-info">
          <h2 class="video-title">{{ currentVideo.title }}</h2>
          <div class="video-meta">
            <span>{{ currentVideo.author }}</span>
            <span>{{ currentVideo.views }} 次观看</span>
            <span>{{ currentVideo.duration }}</span>
          </div>
          <div class="video-actions">
            <el-button type="primary" icon="el-icon-download" @click="downloadVideo">下载视频</el-button>
            <el-button type="success" icon="el-icon-share" @click="shareVideo">分享视频</el-button>
          </div>
        </div>

        <!-- 评论区域 -->
        <div class="video-comments">
          <h3 class="comments-title">评论</h3>
          <div class="comments-count">0 评论 <el-dropdown>
            <span class="el-dropdown-link">
              最新的 <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
          </el-dropdown></div>

          <!-- 评论输入框 -->
          <div class="comment-input">
            <div class="user-avatar"></div>
            <el-input type="textarea" placeholder="在此输入评论" v-model="commentText" class="comment-textarea"></el-input>
          </div>
        </div>
      </div>

      <!-- 右侧对话区域 -->
      <div class="chat-sidebar">
        <h3 class="chat-title">对话</h3>
        <div class="suggested-questions">
          <div class="question-item" v-for="(question, index) in suggestedQuestions" :key="index" @click="askQuestion(question)">
            {{ question }}
          </div>
        </div>

        <!-- 输入框 -->
        <div class="chat-input">
          <el-input
            type="text"
            placeholder="输入你的问题"
            v-model="chatInput"
            class="input-with-select"
          >
            <el-button slot="append" icon="el-icon-s-promotion" @click="sendChatMessage"></el-button>
          </el-input>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoTutor',
  data() {
    return {
      searchQuery: '',
      showVideoPlayer: false,
      isGeneratingVideo: false,
      generatingProgress: 0,
      generatingTimer: null,
      commentText: '',
      chatInput: '',
      showSuggestions: false,
      currentVideo: {
        title: '',
        src: '',
        author: '',
        views: '',
        duration: ''
      },
      suggestedQuestions: [],
      // 添加搜索提示相关数据
      searchSuggestions: [
        '什么是三角形的勾股定理',
        '什么是泰勒公式'
      ],
      categories: [
        {
          icon: 'SAT',
          iconClass: 'sat-icon',
          title: 'SAT 数学',
          description: '考试重点、解题技巧'
        },
        {
          icon: 'AP',
          iconClass: 'ap-icon',
          title: 'AP 数学',
          description: '微积分、统计学等高阶内容'
        },
        {
          icon: 'STEM',
          iconClass: 'stem-icon',
          title: 'STEM 知识',
          description: '科学、技术、工程和数学'
        },
        {
          icon: '语',
          iconClass: 'language-icon',
          title: '语言学习',
          description: '英语、汉语和其他语言'
        }
      ]
    }
  },
  methods: {
    showSearchSuggestions() {
      this.showSuggestions = true;
    },
    selectSuggestion(suggestion) {
      this.searchQuery = suggestion;
      this.showSuggestions = false;
      this.handleSearch();
    },
    handleDocumentClick(e) {
      const searchBox = document.querySelector('.search-box');
      if (searchBox && !searchBox.contains(e.target)) {
        this.showSuggestions = false;
      }
    },
    handleSearch() {
      if (!this.searchQuery.trim()) return

      // 检查是否是预设的视频问题
      const query = this.searchQuery.trim();
      if (query === '什么是三角形的勾股定理' || query === '什么是勾股定理' || query === '什么是泰勒公式') {
        // 显示生成中提示
        this.isGeneratingVideo = true;
        this.generatingProgress = 0;

        // 模拟进度条
        this.generatingTimer = setInterval(() => {
          this.generatingProgress += 100 / 60; // 6秒内完成
          if (this.generatingProgress >= 100) {
            clearInterval(this.generatingTimer);
            this.isGeneratingVideo = false;
            this.showVideoPlayer = true;

            // 设置视频内容
            if (query === '什么是三角形的勾股定理' || query === '什么是勾股定理') {
              this.currentVideo = {
                title: '什么是三角形的勾股定理',
                src: require('@/assets/video/什么是三角形的勾股定理.mp4'),
                author: 'jxm1 6336',
                views: '1 小时前 · 0 观看',
                duration: '1 小时'
              };
              this.suggestedQuestions = [
                '什么是勾股定理在实际生活中有哪些应用？',
                '如何通过勾股定理证明一个三角形是直角三角形？',
                '如何通过勾股定理求出一个三角形的面积？'
              ];
            } else if (query === '什么是泰勒公式') {
              this.currentVideo = {
                title: '什么是泰勒公式',
                src: require('@/assets/video/什么是泰勒公式.mp4'),
                author: 'jxm1 6336',
                views: '1 小时前 · 0 观看',
                duration: '1 小时'
              };
              this.suggestedQuestions = [
                '什么是泰勒公式的定义？它是如何推导的？',
                '泰勒公式在实际生活中有哪些应用？',
                '泰勒公式和麦克劳林公式有什么区别？'
              ];
            }
          }
        }, 100);
      } else {
        this.$message({
          message: `正在搜索: ${this.searchQuery}`,
          type: 'info'
        });
      }
    },
    downloadVideo() {
      // 创建一个链接元素
      const link = document.createElement('a');
      link.href = this.currentVideo.src;
      link.download = `${this.currentVideo.title}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.$message({
        message: '视频下载已开始',
        type: 'success'
      });
    },
    shareVideo() {
      this.$message({
        message: '分享链接已复制到剪贴板',
        type: 'success'
      });
    },
    askQuestion(question) {
      this.chatInput = question;
      this.sendChatMessage();
    },
    sendChatMessage() {
      if (!this.chatInput.trim()) return;

      this.$message({
        message: `已发送问题: ${this.chatInput}`,
        type: 'success'
      });

      this.chatInput = '';
    }
  },
  mounted() {
    // 点击其他区域时隐藏建议
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    // 清除定时器
    if (this.generatingTimer) {
      clearInterval(this.generatingTimer);
    }
    // 移除事件监听器
    document.removeEventListener('click', this.handleDocumentClick);
  }
}
</script>

<style scoped>
.video-tutor-container {
  padding: 0;
  margin: 0;
  background-color: #e6f7ff; /* 淡蓝色背景 */
  min-height: calc(100vh - 111px); /* 与app-main高度一致 */
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  box-sizing: border-box;
}

/* 主页样式 */
.header-section {
  text-align: center;
  margin: 20px auto 40px;
  padding: 30px 20px;
  background: rgba(255, 255, 255, 0.6); /* 半透明白色背景 */
  border-radius: 12px;
  max-width: 1000px;
}

.main-title {
  font-size: 28px;
  color: #6c5ce7;
  margin-bottom: 30px;
}

.search-box {
  max-width: 600px;
  margin: 0 auto;
}

.search-input >>> .el-input__inner {
  height: 50px;
  border-radius: 25px 0 0 25px;
  border: 1px solid #dcdfe6;
  font-size: 16px;
  padding-left: 20px;
}

.search-input >>> .el-input-group__append {
  border-radius: 0 25px 25px 0;
  background-color: #6c5ce7;
  border-color: #6c5ce7;
  color: white;
  padding: 0 20px;
}

.search-input >>> .el-input-group__append .el-button {
  color: white;
  border: none;
  background: transparent;
  font-size: 16px;
}

.category-section {
  margin: 40px auto;
  max-width: 1000px;
  padding: 0 20px;
}

.section-title {
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  padding-left: 10px;
  border-left: 4px solid #6c5ce7;
}

.category-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}

.category-card {
  width: 240px;
  transition: transform 0.3s;
  cursor: pointer;
  background-color: rgba(255, 255, 255, 0.8); /* 半透明白色背景 */
}

.category-card:hover {
  transform: translateY(-5px);
}

.card-content {
  display: flex;
  align-items: center;
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 15px;
  font-size: 14px;
}

.sat-icon {
  background-color: #ffeaa7;
  color: #fdcb6e;
}

.ap-icon {
  background-color: #ffcfe0;
  color: #e84393;
}

.stem-icon {
  background-color: #c4e7ff;
  color: #0984e3;
}

.language-icon {
  background-color: #d8ffdd;
  color: #00b894;
}

.card-info {
  flex: 1;
}

.card-title {
  font-size: 16px;
  margin: 0 0 5px 0;
  color: #333;
}

.card-desc {
  font-size: 12px;
  color: #666;
  margin: 0;
}

.footer-section {
  margin: 60px auto 20px;
  text-align: center;
  padding: 20px;
  border-top: 1px solid rgba(238, 238, 238, 0.5); /* 半透明边框 */
  background-color: rgba(255, 255, 255, 0.5); /* 半透明白色背景 */
  border-radius: 12px;
  max-width: 1000px;
}

.footer-text {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
}

.footer-desc {
  color: #666;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

/* 视频播放界面样式 */
.video-player-container {
  display: flex;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.video-content {
  flex: 1;
  margin-right: 20px;
}

.video-tabs {
  display: flex;
  margin-bottom: 15px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.tab-button {
  margin-right: 10px;
}

.tab-button.active {
  background-color: #6c5ce7;
  border-color: #6c5ce7;
}

.video-player {
  width: 100%;
  background-color: #000;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
}

.player {
  width: 100%;
  height: auto;
  display: block;
}

.video-info {
  margin-bottom: 30px;
}

.video-title {
  font-size: 20px;
  margin-bottom: 10px;
  color: #333;
}

.video-meta {
  display: flex;
  color: #666;
  font-size: 14px;
  margin-bottom: 15px;
}

.video-meta span {
  margin-right: 15px;
}

.video-actions {
  display: flex;
  margin-bottom: 20px;
}

.video-actions .el-button {
  margin-right: 10px;
}

.video-comments {
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.comments-title {
  font-size: 18px;
  margin-bottom: 15px;
  color: #333;
}

.comments-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  color: #666;
}

.comment-input {
  display: flex;
  margin-bottom: 20px;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #6c5ce7;
  margin-right: 15px;
  flex-shrink: 0;
}

.comment-textarea {
  flex: 1;
}

/* 右侧对话区域样式 */
.chat-sidebar {
  width: 300px;
  background-color: #f9f0ff;
  border-radius: 8px;
  padding: 15px;
}

.chat-title {
  font-size: 18px;
  margin-bottom: 15px;
  color: #333;
  text-align: center;
}

.suggested-questions {
  margin-bottom: 20px;
}

.question-item {
  background-color: #f0e6ff;
  padding: 10px 15px;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-size: 14px;
}

.question-item:hover {
  background-color: #e6d9ff;
}

.chat-input {
  margin-top: auto;
}

.input-with-select >>> .el-input__inner {
  border-radius: 20px 0 0 20px;
}

.input-with-select >>> .el-input-group__append {
  border-radius: 0 20px 20px 0;
  background-color: #6c5ce7;
  border-color: #6c5ce7;
}

.input-with-select >>> .el-input-group__append .el-button {
  color: white;
  border: none;
  background: transparent;
}

/* 视频生成中样式 */
.generating-video-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 111px);
  background-color: #f9f0ff;
}

.generating-content {
  text-align: center;
  background-color: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
}

.generating-icon {
  font-size: 48px;
  color: #6c5ce7;
  margin-bottom: 20px;
}

.generating-title {
  font-size: 24px;
  color: #333;
  margin-bottom: 15px;
}

.generating-desc {
  color: #666;
  margin-bottom: 30px;
}

.progress-text {
  margin-top: 10px;
  color: #6c5ce7;
  font-weight: bold;
}

/* 搜索建议样式 */
.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  margin-top: 5px;
  overflow: hidden;
}

.suggestion-item {
  padding: 12px 20px;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
}

.suggestion-item:hover {
  background-color: #f9f0ff;
  color: #6c5ce7;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.search-box {
  max-width: 600px;
  margin: 0 auto;
  position: relative;
}
</style>
